import a from '@/utils/axios/axios'
import jwt from 'jsonwebtoken'

const user = {
  namespaced: true,
  state: () => ({
    user: null,
    login_loading: false,
    currentTeamId: null,
    refreshTokenInterval: null,
    teamStatusPollingInterval: null
  }),
  mutations: {
    setUser(state, payload) {
      state.user = payload
    },
    setCurrentTeamId(state, id) {
      if(!id) return
      state.currentTeamId = id
    },
    setToken(state, payload) {
      state.token = payload
    },
    setJoinCode(state, { team_id, join_code }) {
      let new_user = { ...state.user }
      let index = new_user.teams.findIndex(team => team.id == team_id)
      new_user.teams[index]['join_code'] = join_code
      state.user = new_user
    },
    setRefreshTokenInterval(state, interval) {
      state.refreshTokenInterval = interval
    },
    setTeamStatusPollingInterval(state, interval) {
      state.teamStatusPollingInterval = interval
    }
  },
  actions: {
    initUser({ commit }, token) {
      let user = jwt.decode(token)

      if(!user?.currentTeamId) {
        user['currentTeamId'] = user['teams'][0]?.currentTeamId
      }

      commit('setUser', user)
      window.localStorage.setItem('tiimio-token', token)
      window.localStorage.setItem('tiimio-lang', user.language || 'en')
      window.localStorage.setItem('tiimio-latest-team', user.currentTeamId)
    },
    confirmEmail({ dispatch }, invite_code) {
      return new Promise((resolve, reject) => {
        a.post('/auth/confirm-email', { code: invite_code })
          .then(res => {
            console.log(res.data)
            dispatch('initUser', res.data.token)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    initACurrentTeam({ getters, commit }) {
      let team_id = window.localStorage.getItem('tiimio-latest-team')
      let team = getters.userTeams.find(team => team.id == team_id)

      if(team) {
        team_id = parseInt(team_id)
        commit('setCurrentTeamId', team_id)
      } else if(getters.userTeams.length) {
        const { id } = getters.userTeams[0]
        commit('setCurrentTeamId', parseInt(id))
        window.localStorage.setItem('tiimio-latest-team', id)
      }

      return
    },
    changeCurrentTeamId({ commit, dispatch }, id) {
      if(typeof id !== 'number') id = parseInt(id)

      commit('setCurrentTeamId', id)
      window.localStorage.setItem('tiimio-latest-team', id)
      dispatch('startTeamStatusPolling')
    },
    openCustomerPortal({ getters }) {
      return new Promise((resolve, reject) => {
        a.post('/stripe/create-customer-portal-session', { team_id: getters.currentTeamId})
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    orderProduct({ getters }, { price_id }) {
      return new Promise((resolve, reject) => {
        a.post('/stripe/customer-session-url', { lookup_key: price_id, team_id: getters.currentTeamId})
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    joinTeam({ dispatch }, { invite_code, join_code }) {
      return new Promise((resolve, reject) => {
        a.post('/team/join', { invite_code, join_code })
          .then(res => {
            resolve(res.data)
            dispatch('changeCurrentTeamId', res.data.team.id)
          })
          .catch(err => reject(err))
      })
    },
    logout({ commit, dispatch }) {
      dispatch('stopTeamStatusPolling')
      window.localStorage.removeItem('tiimio-token')
      commit('setUser', null)
    },
    startTeamStatusPolling({ commit, dispatch, getters }) {
      // Clear any existing interval first
      dispatch('stopTeamStatusPolling')

      // Only start if user has a current team
      if (!getters.currentTeam || !getters.currentTeamId) {
        return
      }

      const pollTeamStatus = async () => {
        // Get current team ID dynamically (in case team changed since interval was set)
        const currentTeamId = getters.currentTeamId
        const currentTeam = getters.currentTeam

        // If no current team, stop polling
        if (!currentTeamId || !currentTeam) {
          dispatch('stopTeamStatusPolling')
          return
        }

        try {
          const response = await a.get(`/team/${currentTeamId}/status`)
          const newStatus = response.data

          // Compare all status fields
          const statusChanged =
            newStatus.closed != currentTeam.closed ||
            newStatus.closed_reason != currentTeam.closed_reason ||
            newStatus.admin_message != currentTeam.admin_message

          if (statusChanged) {
            // Get new token with updated team data
            await dispatch('refetchToken', currentTeamId)
            // Navigate to home - router will handle redirects based on new team status
            window.location.href = '/#/'
          }
        } catch (error) {
          console.error('Team status polling failed:', error)
          // Don't stop polling on error, just log it
        }
      }

      // Start polling every minute (60000ms)
      const interval = setInterval(pollTeamStatus, 30000)
      commit('setTeamStatusPollingInterval', interval)

      // Run initial check immediately
      pollTeamStatus()
    },
    stopTeamStatusPolling({ commit, getters }) {
      if (getters.teamStatusPollingInterval) {
        clearInterval(getters.teamStatusPollingInterval)
        commit('setTeamStatusPollingInterval', null)
      }
    },
    async refetchToken({ dispatch }, teamId) {
      const response = await a.post(`/auth/refresh?currentTeamId=${teamId}`, {
        token: window.localStorage.getItem('tiimio-token')
      })
      dispatch('initUser', response.data.token)
      return response
    },
  },
  getters: {
    userTeams: state => state.user ? state.user.teams : [],
    currentTeam: (state, getters) => {
      if(!state.user?.currentTeamId) return getters.userTeams[0] ? getters.userTeams[0] : null
      return getters.userTeams.find(team => team.id == Number(state.user?.currentTeamId))
    },
    currentTeamId: state => {
      const id = state.user?.currentTeamId
      if(!id) return null
      else if (typeof id === 'string') return parseInt(id)
      else return id
    },
    isAppleTesters: (_, getters) => getters.currentTeamId == 119,
    user: (state, getters) => {
      return {
        ...state.user,
        first_name: state.user?.full_name?.split(' ')[0],
        last_name: state.user?.full_name?.split(' ')[state.user.full_name.split(' ').length - 1],
        isAdmin: getters.isAdmin,
        isOrderer: getters.isOrderer,
      }
    },
    loggedIn: state => !!state.user,
    isOrderer: (state, getters) => {
      if(!getters.currentTeam) return false
      return getters.currentTeam.team_orderer
    },
    isAdmin: (state, getters) => {
      if(!getters.currentTeam) return false
      return getters.currentTeam.team_admin
    },
    isSuperAdmin: (state, getters) => {
      return getters.user?.super_admin || false
    },
    currentPlanId: (_, getters) => {
      return getters.currentTeam?.plan_id
    },
    currentSportId: (_, getters) => getters.currentTeam?.sport_id,
    refreshTokenInterval: state => state.refreshTokenInterval,
    teamStatusPollingInterval: state => state.teamStatusPollingInterval,
    inLeague: (_, getters) => !!getters.currentTeam?.league_id,
    leagueId: (_, getters) => getters.currentTeam?.league_id,
    timesDisabled: (_, getters) => getters.currentTeam?.disable_times || !getters.currentTeam?.sport_times_available,
    current_sport_id: (_, getters) => {
      return getters.currentTeam?.sport_id
    },
    userFirstNamePretty: (_, getters) => {
      let name = getters.user?.full_name.split(' ')[0]
      return name?.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },
    currentLeagueName: (_, getters) => {
      return getters.currentTeam?.pesis_league_name
    },
    isCurrentTeamClosed: (_, getters) => {
      return getters.currentTeam?.closed
    }
  }
}

export default user